import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Component from '../../components/Component';
import {
  DUMMY_LIVE_EVENTS,
  DUMMY_LIVE_OFF_MESSAGE,
  describeDummyLiveError,
  fetchDummyLive,
  isDummyLiveOff,
  resetDummyLive,
  seedDummyLive,
  sendDummyLiveEvent,
  setDummyLiveAutoplay,
} from '../../services/dummyLiveApi';

const PHASE_LABEL = {
  live: 'Live',
  innings_break: 'Innings break',
  completed: 'Completed',
};

function phaseLabel(phase) {
  return PHASE_LABEL[phase] || phase || 'Unknown';
}

function phaseColor(phase) {
  if (phase === 'live') return 'success';
  if (phase === 'innings_break') return 'warning';
  return 'default';
}

function matchHeading(match) {
  const home = match?.teams?.a?.name || 'Dugout XI';
  const away = match?.teams?.b?.name || 'Ground XI';
  return `${home} vs ${away}`;
}

function formatLabel(match) {
  return match?.format_str || String(match?.format || '').toUpperCase() || 'Match';
}

export function DummyLivePanel({ pollMs = 4000 }) {
  const [loading, setLoading] = useState(true);
  const [off, setOff] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [runsByMatch, setRunsByMatch] = useState({});
  const [confirm, setConfirm] = useState(null);
  const offRef = useRef(false);
  const busyRef = useRef(false);

  useEffect(() => {
    offRef.current = off;
  }, [off]);

  useEffect(() => {
    busyRef.current = Boolean(busyKey);
  }, [busyKey]);

  const load = useCallback(async (quiet, isActive = () => true) => {
    if (!quiet) setLoading(true);
    try {
      const data = await fetchDummyLive();
      if (!isActive()) return null;
      setOff(false);
      setStatus(data);
      if (!quiet) setError('');
      return data;
    } catch (loadError) {
      if (!isActive()) return null;
      if (isDummyLiveOff(loadError)) {
        setOff(true);
        setStatus(null);
        setError('');
        return null;
      }
      if (!quiet) setError(describeDummyLiveError(loadError));
      return null;
    } finally {
      if (!quiet && isActive()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const run = (quiet) => {
      if (!active) return Promise.resolve(null);
      if (quiet && (offRef.current || busyRef.current)) return Promise.resolve(null);
      return load(quiet, () => active);
    };
    run(false);
    if (!pollMs) {
      return () => {
        active = false;
      };
    }
    const timer = setInterval(() => run(true), pollMs);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [load, pollMs]);

  const applyResult = (data, message) => {
    if (data) setStatus(data);
    if (message) {
      setNotice(message);
      setError('');
    }
  };

  const runAction = async (key, action, message) => {
    setBusyKey(key);
    setError('');
    try {
      const data = await action();
      applyResult(data, message);
    } catch (actionError) {
      if (isDummyLiveOff(actionError)) {
        setOff(true);
        setStatus(null);
        setConfirm(null);
        return;
      }
      setError(describeDummyLiveError(actionError));
    } finally {
      setBusyKey('');
    }
  };

  const matches = Array.isArray(status?.matches) ? status.matches : [];
  const title = status?.title || 'Dugout QA Live';
  const competitionId = status?.competition_cid ?? 990000001;

  const confirmCopy = () => {
    if (!confirm) return { title: '', body: '', action: '' };
    if (confirm.kind === 'seed') {
      return {
        title: 'Reset all three matches?',
        body: 'This recreates Dugout QA Live and puts every match back at a live toss. Commentary on these matches is cleared.',
        action: 'Reset all matches',
      };
    }
    if (confirm.kind === 'reset') {
      return {
        title: `Replay ${formatLabel(confirm.match)} from the toss?`,
        body: 'The current score stays only until this finishes. Commentary for this match is cleared, and the match goes live again.',
        action: 'Confirm replay',
      };
    }
    return {
      title: `Complete ${formatLabel(confirm.match)}?`,
      body: 'The match is marked completed, autoplay turns off, and completion hooks run. Replay from the toss when you want it live again.',
      action: 'Complete match',
    };
  };

  const submitConfirm = () => {
    if (!confirm) return;
    const pending = confirm;
    setConfirm(null);
    if (pending.kind === 'seed') {
      runAction('seed', seedDummyLive, 'All three matches are back at the toss.');
      return;
    }
    if (pending.kind === 'reset') {
      runAction(
        `${pending.match.match_id}:reset`,
        () => resetDummyLive(pending.match.match_id),
        `${formatLabel(pending.match)} is live from the toss.`
      );
      return;
    }
    runAction(
      `${pending.match.match_id}:match_complete`,
      () => sendDummyLiveEvent(pending.match.match_id, 'match_complete'),
      `${formatLabel(pending.match)} is completed. Autoplay is off.`
    );
  };

  const dialog = confirmCopy();
  const seeding = busyKey === 'seed';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/" aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ ml: 2, flex: 1 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 0 }}>
            Dummy live competition
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dev and QA. Three permanent matches that stay on the home card and match wall.
          </Typography>
        </Box>
        {!loading && !off && (
          <IconButton aria-label="Refresh" onClick={() => load(false)} disabled={Boolean(busyKey)}>
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {loading && (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} aria-label="Loading dummy live" />
        </Box>
      )}

      {!loading && off && <Alert severity="warning">{DUMMY_LIVE_OFF_MESSAGE}</Alert>}

      {!loading && !off && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {title} · competition {competitionId}. Opening this page seeds the matches the first time.
            While autoplay is on, the cron bowls one ball about every 8 seconds and replays a finished
            match from the toss. Use Match complete when you need the completed state, then Replay from toss.
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={() => setConfirm({ kind: 'seed' })} disabled={Boolean(busyKey)}>
              {seeding ? 'Resetting…' : 'Reset all to toss'}
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {notice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
              {notice}
            </Alert>
          )}

          <Grid container spacing={2}>
            {matches.map((match) => {
              const id = match.match_id;
              const cardBusy = seeding || String(busyKey).startsWith(`${id}:`);
              const runsValue = runsByMatch[id] ?? '1';
              return (
                <Grid item xs={12} md={4} key={id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip size="small" label={formatLabel(match)} />
                        <Chip size="small" color={phaseColor(match.phase)} label={phaseLabel(match.phase)} />
                      </Stack>
                      <Typography variant="h6" component="h2">
                        {matchHeading(match)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {match.subtitle || 'QA'} · {id}
                      </Typography>
                      <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {match.score || '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {match.overs || '0.0'} overs · innings {match.inning ?? '—'}
                        {match.target != null ? ` · target ${match.target}` : ''}
                      </Typography>
                      {match.status_note ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {match.status_note}
                        </Typography>
                      ) : null}
                      {match.replay_count > 0 ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Replayed {match.replay_count} {match.replay_count === 1 ? 'time' : 'times'}
                        </Typography>
                      ) : null}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(match.autoplay)}
                            disabled={cardBusy}
                            onChange={(_, checked) =>
                              runAction(
                                `${id}:autoplay`,
                                () => setDummyLiveAutoplay(id, checked),
                                checked ? 'Autoplay is on.' : 'Autoplay is paused. The score stays where it is.'
                              )
                            }
                            inputProps={{ 'aria-label': `Autoplay ${id}` }}
                          />
                        }
                        label={match.autoplay ? 'Autoplay on' : 'Autoplay paused'}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Pausing leaves the current score. The ticker still bowls between manual events while autoplay is on.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={cardBusy}
                        onClick={() => setConfirm({ kind: 'reset', match })}
                        sx={{ mb: 1.5 }}
                      >
                        Replay from toss
                      </Button>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {DUMMY_LIVE_EVENTS.map((item) => (
                          <Button
                            key={item.event}
                            size="small"
                            variant={item.event === 'match_complete' ? 'contained' : 'outlined'}
                            color={item.event === 'match_complete' ? 'error' : 'primary'}
                            disabled={cardBusy}
                            onClick={() => {
                              if (item.event === 'match_complete') {
                                setConfirm({ kind: 'match_complete', match });
                                return;
                              }
                              runAction(
                                `${id}:${item.event}`,
                                () => sendDummyLiveEvent(id, item.event),
                                `${formatLabel(match)} · ${item.label}`
                              );
                            }}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          label="Runs"
                          value={runsValue}
                          disabled={cardBusy}
                          onChange={(event) =>
                            setRunsByMatch((current) => ({ ...current, [id]: event.target.value }))
                          }
                          inputProps={{
                            'aria-label': `Runs ${id}`,
                            inputMode: 'numeric',
                          }}
                          sx={{ width: 88 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={cardBusy}
                          onClick={() =>
                            runAction(
                              `${id}:ball`,
                              () => sendDummyLiveEvent(id, 'ball', runsValue),
                              `${formatLabel(match)} · ball`
                            )
                          }
                        >
                          Ball
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Dialog
        open={Boolean(confirm)}
        transitionDuration={0}
        onClose={() => {
          if (!busyKey) setConfirm(null);
        }}
        aria-labelledby="dummy-live-confirm-title"
      >
        <DialogTitle id="dummy-live-confirm-title">{dialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dialog.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={Boolean(busyKey)}>
            Cancel
          </Button>
          <Button variant="contained" color={confirm?.kind === 'match_complete' ? 'error' : 'primary'} onClick={submitConfirm}>
            {dialog.action}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DummyLive() {
  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header" style={{ paddingBottom: 0 }} />
          <DummyLivePanel />
        </div>
      </div>
    </Component>
  );
}

export default DummyLive;
